import { useEffect, useMemo, useState } from 'react';
import { Platform, useWindowDimensions, View } from 'react-native';
import { useCatalogProducts } from '../../../queries/useCatalogProducts';
import { useProductCategories } from '../../../queries/useProductCategories';
import { shouldShowCatalogProduct, userHasCatalogProductAccess } from '../../../utils/accessUtils';
import { filterCatalogVitrineRepresentatives } from '../../../utils/catalogEquivalenceGroup';
import {
  flattenCatalogVitrineShelves,
} from '../../../utils/catalogVitrineOrder';
import { persistCatalogVitrineOrder } from '../../../services/catalogVitrineOrder';
import { queryClient } from '../../../queries/queryClient';
import { ProductCategoryShelf } from './ProductCategoryShelf';
import { ProductVitrineFilterBar } from './ProductVitrineFilterBar';
import { useProductVitrineFilters } from './useProductVitrineFilters';
import ProductItem from '../ProductItem';
import { useCustomTheme } from '../../../contexts/useCustomTheme';
import THEME from '../../../config/theme';
import { Title } from '../../../config/theme/globalStyles';
import { removeSansSerif } from '../../../utils/validators';
import {
  PRODUCT_VITRINE_FILTER_MINE,
} from './productVitrineFilters';

const UNCATEGORIZED_SHELF_ID = '__uncategorized__';
const UNCATEGORIZED_SHELF_TITLE = 'Produtos';

const sortByCatalogOrder = (items) =>
  [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

const GRID_HORIZONTAL_GUTTER = 20;

function getGridColumns(screenWidth) {
  if (screenWidth >= 1280) return 4;
  if (screenWidth >= 960) return 3;
  if (screenWidth >= 640) return 2;
  return 1;
}

export function ProductsSection({
  user,
  enabled,
  showProductGrid = false,
  onViewAllContent,
  vitrine: externalVitrine,
  hideFilterBar = false,
}) {
  const { width: screenWidth } = useWindowDimensions();
  const gridColumns = getGridColumns(screenWidth);
  const gridGap = 16;
  const { data: catalogProducts = [] } = useCatalogProducts(enabled);
  const { data: productCategories = [] } = useProductCategories();
  const { customColor, customFont } = useCustomTheme();
  const [localShelves, setLocalShelves] = useState(null);

  const isAdminReorder = !!user?.isAdmin && !user?.adminRole && !showProductGrid;
  const canDragProducts = isAdminReorder && Platform.OS === 'web';

  const internalVitrine = useProductVitrineFilters({
    user,
    enabled: enabled && showProductGrid && !externalVitrine,
    defaultToMine: !user?.isAdmin,
  });
  const vitrine = externalVitrine ?? internalVitrine;

  const chipColors = {
    activeButton:
      customColor?.button?.backgroundApp ?? THEME.COLORS.PRIMARY_800,
    inactiveButton:
      customColor?.iconsMain?.catalogFilters ||
      customColor?.iconsMain?.icons ||
      THEME.COLORS.ICON_MAIN,
    activeText: customColor?.button?.textApp ?? THEME.COLORS.TEXT_BUTTON,
    background: customColor?.background?.app ?? THEME.COLORS.BACKGROUND_MAIN,
    text: customColor?.text?.app ?? THEME.COLORS.TEXT_MAIN,
  };

  const chipFont =
    customFont?.fontFamilyText
      ? removeSansSerif(customFont.fontFamilyText)
      : THEME.FONTFAMILY.REGULAR;

  const visibleProducts = useMemo(() => {
    if (!enabled) return [];

    if (isAdminReorder) {
      return filterCatalogVitrineRepresentatives(
        catalogProducts.filter(
          (product) => product.catalogImage?.url && product.showInCatalog !== false
        ),
        catalogProducts
      ).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    return vitrine.visibleProducts;
  }, [enabled, catalogProducts, isAdminReorder, vitrine.visibleProducts]);

  const { effectiveFilter, filteredProducts, filters, setSelectedFilter, restrictToUserProducts } =
    vitrine;

  const shelves = useMemo(() => {
    if (!enabled || !isAdminReorder) return [];

    const itemsByCategoryId = new Map();
    const fallbackItems = [];

    visibleProducts.forEach((product) => {
      if (!product.productCategoryId) {
        fallbackItems.push(product);
        return;
      }
      if (!itemsByCategoryId.has(product.productCategoryId)) {
        itemsByCategoryId.set(product.productCategoryId, []);
      }
      itemsByCategoryId.get(product.productCategoryId).push(product);
    });

    const knownCategoryIds = new Set(productCategories.map((category) => category.id));
    itemsByCategoryId.forEach((items, categoryId) => {
      if (!knownCategoryIds.has(categoryId)) {
        fallbackItems.push(...items);
        itemsByCategoryId.delete(categoryId);
      }
    });

    const sortItems = (items) => sortByCatalogOrder(items);

    const categorizedShelves = productCategories
      .filter((category) => itemsByCategoryId.has(category.id))
      .map((category) => ({
        id: category.id,
        title: category.name,
        items: sortItems(itemsByCategoryId.get(category.id)),
      }));

    if (fallbackItems.length > 0) {
      categorizedShelves.push({
        id: UNCATEGORIZED_SHELF_ID,
        title: UNCATEGORIZED_SHELF_TITLE,
        items: sortItems(fallbackItems),
      });
    }

    return categorizedShelves;
  }, [enabled, visibleProducts, productCategories, isAdminReorder]);

  useEffect(() => {
    setLocalShelves(null);
  }, [shelves]);

  const displayShelves = localShelves || shelves;

  const handleShelfReorder = async (shelfId, reorderedItems) => {
    const nextShelves = displayShelves.map((shelf) =>
      shelf.id === shelfId ? { ...shelf, items: reorderedItems } : shelf
    );
    setLocalShelves(nextShelves);

    try {
      await persistCatalogVitrineOrder(flattenCatalogVitrineShelves(nextShelves));
      queryClient.invalidateQueries({ queryKey: ['catalogProducts'] });
    } catch (error) {
      console.error('Erro ao salvar ordem da vitrine:', error);
      setLocalShelves(null);
    }
  };

  if (!enabled) return null;

  if (visibleProducts.length === 0) return null;

  if (isAdminReorder) {
    return (
      <>
        {displayShelves.map((shelf) => (
          <ProductCategoryShelf
            key={shelf.id}
            shelfId={shelf.id}
            title={shelf.title}
            items={shelf.items}
            user={user}
            canDrag={canDragProducts && shelf.items.length > 1}
            onReorder={(_from, _to, reorderedItems) =>
              handleShelfReorder(shelf.id, reorderedItems)
            }
          />
        ))}
      </>
    );
  }

  return (
    <View style={{ paddingBottom: 8, paddingTop: hideFilterBar ? 16 : 10 }}>
      {!hideFilterBar ? (
        <ProductVitrineFilterBar
          filters={filters}
          selected={effectiveFilter}
          onSelect={setSelectedFilter}
          onViewAllContent={onViewAllContent}
          colors={chipColors}
          fonts={chipFont}
        />
      ) : null}

      {filteredProducts.length === 0 && effectiveFilter === PRODUCT_VITRINE_FILTER_MINE ? (
        <View style={{ paddingHorizontal: GRID_HORIZONTAL_GUTTER, paddingTop: 16 }}>
          <Title
            fontFamily={chipFont}
            fontSize={12}
            color={chipColors.text}
            textAlign="center"
          >
            {restrictToUserProducts
              ? 'Você ainda não tem conteúdos liberados neste catálogo.'
              : 'Você ainda não tem conteúdos liberados. Toque em "Todos" para explorar os programas disponíveis.'}
          </Title>
        </View>
      ) : (
        <View
          style={{
            flexDirection: 'row',
            flexWrap: 'wrap',
            paddingHorizontal: GRID_HORIZONTAL_GUTTER,
            marginHorizontal: -gridGap / 2,
          }}
        >
          {filteredProducts.map((product) => (
            <View
              key={product.id}
              style={{
                width: `${100 / gridColumns}%`,
                paddingHorizontal: gridGap / 2,
                paddingBottom: gridGap,
              }}
            >
              <ProductItem
                product={product}
                isLocked={!userHasCatalogProductAccess(product, user)}
                variant="grid"
              />
            </View>
          ))}
        </View>
      )}
    </View>
  );
}

export default ProductsSection;
